import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Clock, AlertTriangle, CheckCircle, TrendingUp } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { escalationService } from '../../services/escalationService';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';

const EscalationRulesManagement = () => {
  const { user } = useAuth();
  const [rules, setRules] = useState([]);
  const [escalationHistory, setEscalationHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    ruleName: '',
    description: '',
    status: 'active',
    alertTypes: [],
    severityLevels: [],
    responseTimeoutMinutes: 60,
    checkIntervalMinutes: 15,
    escalateToRole: 'supervisor',
    notifyViaSms: true,
    notifyViaEmail: true,
    priority: 1
  });

  const alertTypeOptions = [
    { value: 'compliance_violation', label: 'Compliance Violation' },
    { value: 'environmental_alert', label: 'Environmental Alert' },
    { value: 'treatment_alert', label: 'Treatment Alert' }
  ];

  const severityOptions = [
    { value: 'critical', label: 'Critical' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' }
  ];

  const roleOptions = [
    { value: 'supervisor', label: 'Supervisor' },
    { value: 'manager', label: 'Manager' },
    { value: 'company_admin', label: 'Company Admin' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  useEffect(() => {
    if (user?.id) {
      loadData();
    }
  }, [user?.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [rulesData, historyData, statsData] = await Promise.all([
        escalationService?.getEscalationRules(),
        escalationService?.getEscalationHistory({ limit: 10 }),
        escalationService?.getEscalationStats()
      ]);
      setRules(rulesData);
      setEscalationHistory(historyData);
      setStats(statsData);
    } catch (err) {
      setError(err?.message || 'Failed to load escalation data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    try {
      setError('');
      if (editingRule) {
        await escalationService?.updateEscalationRule(editingRule?.id, formData);
      } else {
        await escalationService?.createEscalationRule(formData);
      }
      setShowAddForm(false);
      setEditingRule(null);
      resetForm();
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to save escalation rule');
    }
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setFormData({
      ruleName: rule?.ruleName,
      description: rule?.description,
      status: rule?.status,
      alertTypes: rule?.alertTypes || [],
      severityLevels: rule?.severityLevels || [],
      responseTimeoutMinutes: rule?.responseTimeoutMinutes,
      checkIntervalMinutes: rule?.checkIntervalMinutes,
      escalateToRole: rule?.escalateToRole,
      notifyViaSms: rule?.notifyViaSms,
      notifyViaEmail: rule?.notifyViaEmail,
      priority: rule?.priority
    });
    setShowAddForm(true);
  };

  const handleDelete = async (ruleId) => {
    if (!confirm('Are you sure you want to delete this escalation rule?')) return;
    
    try {
      await escalationService?.deleteEscalationRule(ruleId);
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to delete escalation rule');
    }
  };

  const resetForm = () => {
    setFormData({
      ruleName: '',
      description: '',
      status: 'active',
      alertTypes: [],
      severityLevels: [],
      responseTimeoutMinutes: 60,
      checkIntervalMinutes: 15,
      escalateToRole: 'supervisor',
      notifyViaSms: true,
      notifyViaEmail: true,
      priority: 1
    });
  };

  const handleArrayToggle = (field, value) => {
    setFormData(prev => {
      const currentArray = prev?.[field] || [];
      const newArray = currentArray?.includes(value)
        ? currentArray?.filter(v => v !== value)
        : [...currentArray, value];
      return { ...prev, [field]: newArray };
    });
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-red-600';
      case 'high': return 'text-orange-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-blue-600';
      default: return 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="container mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Alert Escalation Rules</h1>
          <p className="text-muted-foreground">
            Configure automated escalation rules to reassign alerts to supervisors when field teams don't respond within specified timeframes
          </p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Statistics Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-muted-foreground text-sm">Total Escalations</span>
                <TrendingUp className="text-primary" size={20} />
              </div>
              <p className="text-3xl font-bold text-foreground">{stats?.totalEscalations}</p>
            </div>

            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-muted-foreground text-sm">Pending</span>
                <Clock className="text-orange-500" size={20} />
              </div>
              <p className="text-3xl font-bold text-foreground">{stats?.pendingEscalations}</p>
            </div>

            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-muted-foreground text-sm">Resolved</span>
                <CheckCircle className="text-green-500" size={20} />
              </div>
              <p className="text-3xl font-bold text-foreground">{stats?.resolvedEscalations}</p>
            </div>

            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-muted-foreground text-sm">Avg Response Time</span>
                <Clock className="text-blue-500" size={20} />
              </div>
              <p className="text-3xl font-bold text-foreground">{stats?.avgResponseTime}m</p>
            </div>
          </div>
        )}

        {/* Escalation Rules Section */}
        <div className="bg-card rounded-lg border border-border p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-foreground">Escalation Rules</h2>
            <Button
              onClick={() => {
                setShowAddForm(true);
                setEditingRule(null);
                resetForm();
              }}
              iconName="Plus"
            >
              Add Rule
            </Button>
          </div>

          {/* Add/Edit Form */}
          {showAddForm && (
            <form onSubmit={handleSubmit} className="mb-6 p-6 bg-muted/50 rounded-lg border border-border">
              <h3 className="text-lg font-semibold text-foreground mb-4">
                {editingRule ? 'Edit Escalation Rule' : 'New Escalation Rule'}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <Input
                  label="Rule Name"
                  value={formData?.ruleName}
                  onChange={(e) => setFormData({ ...formData, ruleName: e?.target?.value })}
                  required
                />

                <Select
                  label="Status"
                  value={formData?.status}
                  onChange={(e) => setFormData({ ...formData, status: e?.target?.value })}
                  options={statusOptions}
                />
              </div>

              <div className="mb-4">
                <Input
                  label="Description"
                  value={formData?.description}
                  onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Alert Types</label>
                  <div className="space-y-2">
                    {alertTypeOptions?.map(option => (
                      <label key={option?.value} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData?.alertTypes?.includes(option?.value)}
                          onChange={() => handleArrayToggle('alertTypes', option?.value)}
                          className="rounded border-border"
                        />
                        <span className="text-sm text-foreground">{option?.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Severity Levels</label>
                  <div className="space-y-2">
                    {severityOptions?.map(option => (
                      <label key={option?.value} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData?.severityLevels?.includes(option?.value)}
                          onChange={() => handleArrayToggle('severityLevels', option?.value)}
                          className="rounded border-border"
                        />
                        <span className="text-sm text-foreground">{option?.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <Input
                  label="Response Timeout (minutes)"
                  type="number"
                  value={formData?.responseTimeoutMinutes}
                  onChange={(e) => setFormData({ ...formData, responseTimeoutMinutes: parseInt(e?.target?.value) })}
                  required
                />

                <Input
                  label="Check Interval (minutes)"
                  type="number"
                  value={formData?.checkIntervalMinutes}
                  onChange={(e) => setFormData({ ...formData, checkIntervalMinutes: parseInt(e?.target?.value) })}
                  required
                />

                <Input
                  label="Priority"
                  type="number"
                  value={formData?.priority}
                  onChange={(e) => setFormData({ ...formData, priority: parseInt(e?.target?.value) })}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <Select
                  label="Escalate To Role"
                  value={formData?.escalateToRole}
                  onChange={(e) => setFormData({ ...formData, escalateToRole: e?.target?.value })}
                  options={roleOptions}
                />

                <label className="flex items-center space-x-2 mt-8">
                  <input
                    type="checkbox"
                    checked={formData?.notifyViaSms}
                    onChange={(e) => setFormData({ ...formData, notifyViaSms: e?.target?.checked })}
                    className="rounded border-border"
                  />
                  <span className="text-sm text-foreground">Notify via SMS</span>
                </label>

                <label className="flex items-center space-x-2 mt-8">
                  <input
                    type="checkbox"
                    checked={formData?.notifyViaEmail}
                    onChange={(e) => setFormData({ ...formData, notifyViaEmail: e?.target?.checked })}
                    className="rounded border-border"
                  />
                  <span className="text-sm text-foreground">Notify via Email</span>
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <Button type="submit">
                  {editingRule ? 'Update Rule' : 'Create Rule'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingRule(null);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}

          {/* Rules List */}
          <div className="space-y-4">
            {rules?.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No escalation rules configured yet</p>
            ) : (
              rules?.map(rule => (
                <div key={rule?.id} className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-foreground">{rule?.ruleName}</h3>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          rule?.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {rule?.status}
                        </span>
                        {rule?.isDefault && (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            Default
                          </span>
                        )}
                      </div>
                      
                      {rule?.description && (
                        <p className="text-sm text-muted-foreground mb-3">{rule?.description}</p>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Timeout:</span>
                          <span className="ml-2 font-medium text-foreground">{rule?.responseTimeoutMinutes}m</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Escalate To:</span>
                          <span className="ml-2 font-medium text-foreground capitalize">{rule?.escalateToRole}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Priority:</span>
                          <span className="ml-2 font-medium text-foreground">{rule?.priority}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          {rule?.notifyViaSms && (
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">SMS</span>
                          )}
                          {rule?.notifyViaEmail && (
                            <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">Email</span>
                          )}
                        </div>
                      </div>

                      {rule?.severityLevels?.length > 0 && (
                        <div className="mt-3 flex items-center space-x-2">
                          <span className="text-xs text-muted-foreground">Severities:</span>
                          {rule?.severityLevels?.map(severity => (
                            <span key={severity} className={`px-2 py-1 rounded text-xs font-medium capitalize ${getSeverityColor(severity)} bg-opacity-10`}>
                              {severity}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => handleEdit(rule)}
                        className="p-2 hover:bg-muted rounded-lg transition-colors"
                      >
                        <Edit2 size={18} className="text-muted-foreground" />
                      </button>
                      {!rule?.isDefault && (
                        <button
                          onClick={() => handleDelete(rule?.id)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 size={18} className="text-red-600" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent Escalation History */}
        <div className="bg-card rounded-lg border border-border p-6">
          <h2 className="text-xl font-semibold text-foreground mb-6">Recent Escalations</h2>
          
          {escalationHistory?.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No escalations recorded yet</p>
          ) : (
            <div className="space-y-4">
              {escalationHistory?.map(history => (
                <div key={history?.id} className="border border-border rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <AlertTriangle className="text-orange-500" size={18} />
                        <span className="font-medium text-foreground">Alert Escalated</span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          history?.escalationStatus === 'completed' ? 'bg-green-100 text-green-800' :
                          history?.escalationStatus === 'triggered'? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {history?.escalationStatus}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">{history?.escalationReason}</p>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(history?.escalatedAt)?.toLocaleString()}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">From:</span>
                      <span className="ml-2 font-medium text-foreground">
                        {history?.originalAssigneeUser?.full_name || 'Unknown'}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">To:</span>
                      <span className="ml-2 font-medium text-foreground">
                        {history?.escalatedToUser?.full_name || 'Unknown'}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Response Time:</span>
                      <span className="ml-2 font-medium text-foreground">
                        {history?.actualResponseTimeMinutes}m / {history?.timeoutMinutes}m
                      </span>
                    </div>
                  </div>

                  {(history?.smsSent || history?.emailSent) && (
                    <div className="mt-3 flex items-center space-x-2">
                      <span className="text-xs text-muted-foreground">Notifications:</span>
                      {history?.smsSent && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">SMS Sent</span>
                      )}
                      {history?.emailSent && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">Email Sent</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EscalationRulesManagement;